import { lumi } from '@/lib/lumi'
import { useCallback, useState } from 'react'

export interface SavedEstimate {
  _id: string
  userId: string
  calculatorType: 'tattoo' | 'laser'
  label?: string
  summary: string
  inputs: Record<string, unknown>
  results: Record<string, unknown>
  createdAt: string
}

export function useEstimates() {
  const [estimates, setEstimates] = useState<SavedEstimate[]>([])
  const [loading, setLoading] = useState(false)

  const fetchEstimates = useCallback(async (userId: string) => {
    setLoading(true)
    try {
      const { list } = await lumi.entities.estimates.list({
        filter: { userId },
        sort: { createdAt: -1 },
        limit: 50,
      })
      setEstimates(list as SavedEstimate[])
    }
    catch (error) {
      console.error('Failed to fetch saved estimates:', error)
    }
    finally {
      setLoading(false)
    }
  }, [])

  const saveEstimate = useCallback(async (data: {
    userId: string
    calculatorType: 'tattoo' | 'laser'
    label?: string
    summary: string
    inputs: Record<string, unknown>
    results: Record<string, unknown>
  }) => {
    try {
      const created = await lumi.entities.estimates.create({
        ...data,
        createdAt: new Date().toISOString(),
      })
      setEstimates(prev => [created as SavedEstimate, ...prev])
      return created
    }
    catch (error) {
      console.error('Failed to save estimate:', error)
      throw error
    }
  }, [])

  const deleteEstimate = useCallback(async (id: string) => {
    try {
      await lumi.entities.estimates.delete(id)
      setEstimates(prev => prev.filter(item => item._id !== id))
    }
    catch (error) {
      console.error('Failed to delete estimate:', error)
      throw error
    }
  }, [])

  const renameEstimate = useCallback(async (id: string, label: string) => {
    if (typeof id !== 'string' || !id || id === '[object Object]') {
      throw new Error('Invalid ID: must be a string')
    }
    const trimmed = label.trim()
    try {
      await lumi.entities.estimates.update(id, {
        label: trimmed,
        updatedAt: new Date().toISOString(),
      })
      setEstimates(prev => prev.map(item => (
        item._id === id ? { ...item, label: trimmed } : item
      )))
    }
    catch (error) {
      console.error('Failed to rename estimate:', error)
      throw error
    }
  }, [])

  return { estimates, loading, fetchEstimates, saveEstimate, deleteEstimate, renameEstimate }
}
